import { useState } from 'react';
import { toast } from 'react-toastify';

import { FileTypes, MediaFile } from '@/api/shared.types.ts';
import { getVouchersDetailsByIds } from '@/api/voucher/voucher.actions.ts';
import { VoucherBatch } from '@/api/voucher/voucher.types.ts';
import { useAsync } from '@/hooks/global/useAsync.ts';
import { generateVouchersBatchPdf } from '@/utils/generateVoucherPdf.ts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useVoucherSelection = (batches: VoucherBatch[]) => {
  const [selectedVouchers, setSelectedVouchers] = useState<string[]>([]);
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(
    new Set()
  );
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

  const { run, isLoading: isFetching } = useAsync();

  const toggleBatch = (batchId: string): void => {
    setExpandedBatches((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  const selectBatch = (batchId: string, isSelected: boolean): void => {
    const batch = batches.find((b) => b.batchId === batchId);
    if (!batch) {
      return;
    }
    const voucherIds = batch.vouchers.map((v) => v.id);

    setSelectedVouchers((prev) =>
      isSelected
        ? Array.from(new Set([...prev, ...voucherIds]))
        : prev.filter((id) => !voucherIds.includes(id))
    );
  };

  const selectVoucher = (voucherId: string, isSelected: boolean): void => {
    setSelectedVouchers((prev) =>
      isSelected ? [...prev, voucherId] : prev.filter((id) => id !== voucherId)
    );
  };

  const showPdf = (blob: Blob, name = 'vouchers.pdf'): void => {
    const url = URL.createObjectURL(blob);
    const pdfFile: MediaFile = {
      id: crypto.randomUUID(),
      type: FileTypes.PDF,
      url,
      name,
    };
    setFiles([pdfFile]);
    setSelectedIndex(0);
  };

  const generatePdf = async (): Promise<void> => {
    setIsGeneratingPdf(true);
    try {
      const response = await run(() =>
        getVouchersDetailsByIds(selectedVouchers)
      );

      if (!response.success) {
        toast.error('Failed to retrieve vouchers details.');
        return;
      }

      if (response.content.vouchersDetails.length === 0) {
        toast.info('No voucher details found to generate PDF.');
        return;
      }

      const blob = await generateVouchersBatchPdf(
        response.content.vouchersDetails
      );
      showPdf(blob, 'vouchers-batch.pdf');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const closePdf = (): void => {
    files.forEach((file) => {
      if (file.type === FileTypes.PDF) URL.revokeObjectURL(file.url);
    });
    setFiles([]);
    setSelectedIndex(null);
    setSelectedVouchers([]);
  };

  const onNext = (): void =>
    setSelectedIndex((prev) => (prev !== null ? (prev + 1) % files.length : 0));

  const onPrev = (): void =>
    setSelectedIndex((prev) =>
      prev !== null ? (prev - 1 + files.length) % files.length : 0
    );

  return {
    selectedVouchers,
    expandedBatches,
    files,
    selectedIndex,
    isFetching,
    isGeneratingPdf,

    toggleBatch,
    selectBatch,
    selectVoucher,
    generatePdf,
    showPdf,
    closePdf,
    onNext,
    onPrev,
  };
};
